import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Link, useParams } from 'react-router-dom';
import BackToList from '../components/BackToList';
import withApi from '../../components/higherOrderComponents/withApi';
import Guid from '../components/Guid';
import RelatedLinks from '../components/RelatedLinks';
import StatusPill from '../components/StatusPill';

/**
 * Crawling — one run that fetched a venue's availability and turned it into
 * activities.
 *
 * Read-only, like the old page. What it is read *for* is why a run produced
 * nothing, so the two things that answer that are given room rather than being
 * printed as `[object Object]`, which is what the old page does with them: the
 * input it was given, and the output it produced — activity count, page count and
 * the error text.
 */

const fmtDate = value => (value ? String(value).replace('T', ' ').slice(0, 19) : '—');

const CrawlingInternal = ({ api }) => {
  const { crawlingId } = useParams();

  const [crawling, setCrawling] = useState(null);
  const [failed, setFailed] = useState(false);

  const load = useCallback(async () => {
    try {
      const data = await api.crawlings.getCrawling(crawlingId);
      if (!data || data.success === false || !data.id) {
        setFailed(true);
        return;
      }
      setCrawling(data);
      setFailed(false);
    } catch (error) {
      setFailed(true);
    }
  }, [api, crawlingId]);

  useEffect(() => {
    load();
  }, [load]);

  if (failed) {
    return (
      <div className="next-page">
        <BackToList to="/next/crawlings">← All crawlings</BackToList>
        <div className="next-note next-note--error">
          Could not load this crawling.{' '}
          <button type="button" className="next-btn next-btn--quiet" onClick={load}>Retry</button>
        </div>
      </div>
    );
  }

  if (!crawling) return <div className="next-page next-muted">Loading…</div>;

  const output = crawling.output || null;
  const failedRun = crawling.status === 'Failed' || !!output?.errorText;

  const facts = [
    ['Status', crawling.status
      ? <StatusPill status={crawling.status}>{crawling.status}</StatusPill>
      : <span className="next-muted">—</span>],
    ['Product id', crawling.productId
      ? <Guid value={crawling.productId} href={`/product/details/${crawling.productId}`} />
      : <span className="next-muted">—</span>],
    ['Created at', fmtDate(crawling.createdAt)],
    // Started at replaces Executed at; the latter only for crawls from before it existed.
    ...(crawling.startedAt ? [] : [['Executed at (legacy)', fmtDate(crawling.executedAt)]]),
    ['Started at', fmtDate(crawling.startedAt)],
    ['Finished at', fmtDate(crawling.finishedAt)],
    ['Duration', crawling.durationSeconds != null ? `${crawling.durationSeconds} s` : <span className="next-muted">—</span>],
    ['Activities produced', output?.activityCount ?? <span className="next-muted">—</span>],
    ['Pages fetched', output?.pagesCount ?? <span className="next-muted">—</span>]
  ];

  return (
    <div className="next-page next-page--wide">
      <BackToList to="/next/crawlings">← All crawlings</BackToList>

      <header className="next-detail__head">
        <div className="next-detail__title">
          <h1>Crawling</h1>
          {crawling.status && <StatusPill status={crawling.status}>{crawling.status}</StatusPill>}
        </div>
        <span className="next-detail__id">{crawling.id}</span>
      </header>

      {failedRun && output?.errorText && (
        <div className="next-note next-note--error">
          <strong>The run failed:</strong> {output.errorText}
        </div>
      )}

      <div className="next-detail">
        <div className="next-detail__main">
          <section className="next-card">
            <div className="next-card__head"><h2>Run</h2></div>
            <div className="next-facts">
              {facts.map(([label, value]) => (
                <div className="next-fact" key={label}>
                  <span className="next-fact__label">{label}</span>
                  <span className="next-fact__value">{value}</span>
                </div>
              ))}
            </div>
          </section>

          {/* Both are objects. The old page drops them into a definition list,
              where they render as [object Object]; here they are readable. */}
          <section className="next-card">
            <div className="next-card__head"><h2>Input</h2></div>
            {crawling.input
              ? <pre className="next-pre">{JSON.stringify(crawling.input, null, 2)}</pre>
              : <p className="next-table__note next-muted">No input recorded for this run.</p>}
          </section>

          <section className="next-card">
            <div className="next-card__head"><h2>Output</h2></div>
            {output
              ? <pre className="next-pre">{JSON.stringify(output, null, 2)}</pre>
              : <p className="next-table__note next-muted">No output recorded — the run may not have finished.</p>}
          </section>
        </div>

        <aside className="next-detail__side">
          <section className="next-card">
            <div className="next-card__head"><h2>Links</h2></div>
            <RelatedLinks
              items={[
                {
                  label: 'Activities',
                  hint: 'produced by this run',
                  to: `/next/activities?crawlingId=${crawling.id}`
                },
                crawling.productId && {
                  label: 'Other crawlings',
                  hint: 'of this product',
                  to: `/next/crawlings?productId=${crawling.productId}`
                },
                crawling.productId && {
                  label: 'Product',
                  href: `/product/details/${crawling.productId}`
                },
                // The log lives outside this admin; it is the full story when the
                // error text is not enough.
                crawling.logUrl && { label: 'Full log', href: crawling.logUrl }
              ]}
            />
          </section>

          {/* No Actions panel: the old page has none. Starting a run is its own
              page — see "Run a crawling". */}
        </aside>
      </div>
    </div>
  );
};

CrawlingInternal.propTypes = {
  api: PropTypes.object.isRequired
};

const Crawling = withApi(CrawlingInternal);

Crawling.requiresLogin = true;

export default Crawling;
